import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { closeSheet } from '../lib/router'
import { keyboardHeight, onKeyboard } from '../lib/viewport'
import { IconChevronRight, IconX } from './Icons'

/**
 * A pop-up card: centred on every screen size (on phones in the visible area above the
 * keyboard), scrolling inside. `compact`: short, about 8 rows (the command menu).
 *
 * Every pop-up is built the same way, top to bottom: its name and ✕; colors or icons; settings
 * blocks (a `Label`, then one standard control, at most one faint hint line); actions (icon +
 * verb, "…" when a row opens another pop-up); a `Divider`, then danger rows in red. The footer:
 * `CommitButton` where it saves something (Add, Create tab, Save); `DoneButton` where settings
 * apply as you change them, so it's plain they took (✕ reads as backing out).
 */
export function Sheet({
  title,
  children,
  onClose = closeSheet,
  footer,
  wide,
  compact,
  raised,
}: {
  title?: ReactNode
  children: ReactNode
  onClose?: () => void
  footer?: ReactNode
  wide?: boolean
  compact?: boolean
  /** Over a full-screen view (a photo's own pop-up, over the photo). */
  raised?: boolean
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const ref = useRef<HTMLDivElement>(null)
  // The command menu ends above the keyboard on its own (app.css).
  const lifted = useAboveKeyboard(ref, !compact)
  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} style={raised ? { zIndex: 46 } : undefined} />
      <div
        ref={ref}
        style={raised ? { zIndex: 47 } : undefined}
        className={`sheet ${wide ? 'sheet-wide' : ''} ${compact ? 'sheet-compact' : ''} ${lifted ? 'sheet-kb' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        {title !== undefined && (
          <div className="flex flex-none items-center gap-2 px-4 pt-2 pb-2">
            <div className="min-w-0 flex-1 truncate text-[0.9375rem] font-semibold">{title}</div>
            <button type="button" onClick={onClose} className="-mr-2 flex h-9 w-9 items-center justify-center rounded-sm text-muted hover:bg-surface-2 hover:text-fg" aria-label="Close">
              <IconX size={18} />
            </button>
          </div>
        )}
        <div className="scroll-y min-h-0 flex-1 px-4 pb-4">{children}</div>
        {footer && <div className="flex-none border-t border-line px-4 py-3">{footer}</div>}
      </div>
    </>
  )
}

/**
 * A pop-up and the on-screen keyboard (SPEC §8.2): it stays where it opened while it fits above
 * the keyboard. One the keyboard would cover moves up just enough to end above it (`sheet-kb`;
 * a tall one ends there too and scrolls inside), so its Save is never under the keyboard. It
 * glides there, and back to where it was when the keyboard goes down.
 */
function useAboveKeyboard(ref: RefObject<HTMLDivElement | null>, on: boolean): boolean {
  const [lifted, setLifted] = useState(false)
  const now = useRef(false)
  const from = useRef<number | null>(null)
  useEffect(() => {
    const check = () => {
      const el = ref.current
      const vv = window.visualViewport
      if (!el || !vv) return
      const r = el.getBoundingClientRect()
      const lift = on && keyboardHeight() > 0 && (now.current || r.bottom > vv.offsetTop + vv.height - 12)
      if (lift === now.current) {
        if (lift) keepFocusedInView(el)
        return
      }
      now.current = lift
      from.current = r.top
      setLifted(lift)
    }
    const ro = new ResizeObserver(check)
    if (ref.current) ro.observe(ref.current)
    const off = onKeyboard(check)
    check()
    return () => {
      ro.disconnect()
      off()
    }
  }, [ref, on])
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || from.current === null) return
    const dy = from.current - el.getBoundingClientRect().top
    from.current = null
    keepFocusedInView(el)
    if (Math.abs(dy) > 1) el.animate([{ translate: `0 ${dy}px` }, { translate: '0 0' }], { duration: 180, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' })
  }, [lifted, ref])
  return lifted
}

/** The field being typed in, scrolled into view inside the pop-up (which may just have got shorter). */
function keepFocusedInView(sheet: HTMLElement) {
  const f = document.activeElement as HTMLElement | null
  const box = sheet.querySelector<HTMLElement>(':scope > .scroll-y')
  if (!f || !box?.contains(f)) return
  const fr = f.getBoundingClientRect()
  const br = box.getBoundingClientRect()
  if (fr.bottom > br.bottom - 8) box.scrollTop += fr.bottom - (br.bottom - 8)
  else if (fr.top < br.top + 8) box.scrollTop -= br.top + 8 - fr.top
}

/** An action row: icon, then what it does; `next` (a ›) when it opens another pop-up. */
export function SheetButton({
  icon,
  label,
  onClick,
  danger,
  hint,
  disabled,
  next,
}: {
  icon?: ReactNode
  label: string
  onClick: () => void
  danger?: boolean
  hint?: string
  disabled?: boolean
  next?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex min-h-10 w-full items-center gap-3 px-2 text-left hover:bg-surface-2 disabled:opacity-40 disabled:hover:bg-transparent ${danger ? 'text-danger' : ''}`}
    >
      {icon && <span className={`flex w-5 justify-center ${danger ? '' : 'text-muted'}`}>{icon}</span>}
      <span className="flex-1">{label}</span>
      {hint && <span className="lab text-faint">{hint}</span>}
      {next && <IconChevronRight size={14} className="flex-none text-faint" />}
    </button>
  )
}

export function Chip({ active, children, onClick, label }: { active?: boolean; children: ReactNode; onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      // Square, in mono capitals; solid when on (SPEC §7.10).
      className={`lab h-7 flex-none border px-2 ${active ? 'border-fg bg-fg text-bg' : 'border-fg/40 text-fg-2 hover:border-fg'}`}
    >
      {children}
    </button>
  )
}

/** A settings block's label: a mono label (SPEC §7.10). `aside` sits at its right. */
export function Label({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="lab flex items-baseline gap-2 pb-1.5 text-muted">
      <span className="min-w-0 flex-1">{children}</span>
      {aside}
    </div>
  )
}

/** Between a pop-up's actions and its danger rows (or between its big parts). */
export function Divider() {
  return <div className="my-1 border-t border-line" />
}

/** "Done" at the foot of a pop-up whose changes apply as you make them: they're kept. */
export function DoneButton() {
  return <CommitButton onClick={closeSheet}>Done</CommitButton>
}

/** A filter pop-up's foot: Clear filters (when some are on) beside Done. */
export function FilterFooter({ count, onClear, label = 'Clear filters' }: { count: number; onClear: () => void; label?: string }) {
  return (
    <div className="flex gap-2">
      <button type="button" disabled={!count} onClick={onClear} className="h-10 border border-fg px-4 text-sm disabled:opacity-40">
        {label}
      </button>
      <div className="flex-1">
        <DoneButton />
      </div>
    </div>
  )
}

/** The footer button of a pop-up that commits something: Add, Create tab, Save. */
export function CommitButton({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className="h-10 w-full border border-fg bg-fg font-medium text-bg disabled:opacity-40">
      {children}
    </button>
  )
}

/**
 * Tiles: things that are true or not about something (Pinned, Checkbox, Page), lit when true —
 * tapping one switches it. An action tile (Own tab) is never lit. One row, as many as apply.
 */
export function Tiles({ children }: { children: ReactNode }) {
  return <div className="grid auto-cols-fr grid-flow-col gap-2">{children}</div>
}

export function Tile({ icon, label, on, onClick, aria }: { icon: ReactNode; label: string; on?: boolean; onClick: () => void; aria?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on === undefined ? undefined : on}
      aria-label={aria}
      className={`flex h-14 min-w-0 flex-col items-center justify-center gap-1.5 border px-1 ${on ? 'border-fg bg-fg text-bg' : 'border-line-2 text-fg-2 hover:border-fg'}`}
    >
      {icon}
      <span className="lab max-w-full truncate">{label}</span>
    </button>
  )
}

/** One of a few short choices, each with its icon, on one bar. */
export function Segmented<K extends string>({
  label,
  value,
  options,
  onChange,
  slim = false,
}: {
  label: string
  value: K
  options: Array<{ key: K; label: string; icon: ReactNode }>
  onChange: (k: K) => void
  /** One line: icon beside its word (a switch at the top of a pop-up). */
  slim?: boolean
}) {
  return (
    // One bar, the picked part solid (SPEC §7.10).
    <div className="flex border border-fg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={value === o.key}
          onClick={() => onChange(o.key)}
          className={`flex min-w-0 flex-1 items-center justify-center border-l border-fg px-1 first:border-l-0 ${slim ? 'h-8 gap-1.5' : 'flex-col gap-1 py-1.5'} ${value === o.key ? 'bg-fg text-bg' : 'text-fg-2 hover:bg-surface-2'}`}
        >
          {o.icon}
          <span className="lab max-w-full truncate">{o.label}</span>
        </button>
      ))}
    </div>
  )
}

/** Two actions that belong together, side by side (Archive · Move to Trash). */
export function Pair({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-2 pt-2">{children}</div>
}

export function PairButton({ icon, label, onClick, danger }: { icon: ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={`flex h-10 min-w-0 items-center justify-center gap-2 border border-line-2 px-2 hover:border-fg ${danger ? 'text-danger' : ''}`}>
      <span className={`flex-none ${danger ? '' : 'text-muted'}`}>{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  )
}
